import { useCallback, useEffect, useState } from 'react';
import { listDataRequests } from 'api/dataRequests';

// El backend clampea limit a [1,100] y usa 20 por defecto. Nos quedamos con el
// default para no pedir un limit que el backend termine recortando.
const PAGE_SIZE = 20;

/**
 * Listado paginado (server-side) de solicitudes de derechos del titular.
 *
 * La API solo acepta estado, tipo, limit y offset: no hay búsqueda ni orden
 * configurable, el backend ordena siempre por vence_at ascendente (lo más
 * urgente primero). Por eso el hook no expone `search` ni `sorter`.
 *
 * A diferencia de useTicketsList, este hook SÍ expone `error`: la bandeja está
 * restringida por rol (ejecutivo/supervisor/liftlogic) y un 403 tiene que poder
 * mostrarse como un mensaje claro en vez de una tabla vacía silenciosa.
 */
export default function useDataRequestsList(initialFilters = {}) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  // Conteo GLOBAL del tenant de solicitudes abiertas ya vencidas. El backend lo
  // calcula ignorando los filtros estado/tipo: NO es el subtotal del listado.
  const [vencidas, setVencidas] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(initialFilters);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listDataRequests({
        estado: filters.estado || undefined,
        tipo: filters.tipo || undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(data?.total ?? 0);
      setVencidas(data?.vencidas ?? 0);
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      setError({
        status,
        code,
        // El backend responde 403 ROLE_FORBIDDEN al especialista y al
        // administrador de edificio. Ese caso merece copy propio.
        forbidden: status === 403 || code === 'ROLE_FORBIDDEN',
        mensaje: err?.response?.data?.mensaje,
      });
      setRows([]);
      setTotal(0);
      setVencidas(0);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Cambiar un filtro siempre vuelve a la primera página: quedarse en la página
  // 7 de un filtro nuevo mostraría una tabla vacía.
  const setFilter = useCallback((patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  }, []);

  return {
    rows,
    total,
    vencidas,
    loading,
    error,
    page,
    setPage,
    pageSize: PAGE_SIZE,
    filters,
    setFilter,
    refresh,
  };
}
